import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-900">文章未找到</h2>
          <p className="mt-2 text-foreground-500">该文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="whitespace-nowrap inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const categoryNames: Record<string, string> = {
    fashion: '时尚穿搭',
    beauty: '美妆潮流',
    lifestyle: '生活方式',
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryNames[article.category] || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://shenshang003.cn/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '亚欧风格志',
    },
    keywords: article.tags.join(', '),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://shenshang003.cn${item.href}` : undefined,
    })),
  };

  return (
    <main className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <article>
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="mt-4 text-2xl md:text-4xl font-heading font-semibold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 亚欧风格志`}
              className="w-full aspect-[16/9] object-cover object-top"
            />
          </div>

          <div
            className="prose prose-lg max-w-none font-body text-foreground-800 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-heading font-semibold text-foreground-900 text-center mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}